import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Order, OrderFulfillment } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useUpdateFulfillment } from '../hooks/use-order-mutations';
import {
  fromFulfillment,
  fulfillmentUpdateSchema,
  toFulfillmentUpdate,
  type FulfillmentUpdateInput,
  type FulfillmentUpdateOutput,
} from '../schemas/fulfillment-schema';
import { orderConflictKey } from '../schemas/order-rules';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'carrier',
  'trackingNumber',
  'trackingUrl',
  'notifyCustomer',
  'note',
]);

export interface EditFulfillmentDialogProps {
  storeId: string;
  order: Order;
  fulfillment: OrderFulfillment;
  index: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Carrier, tracking and note of one parcel; sends only what changed. */
export function EditFulfillmentDialog({
  storeId,
  order,
  fulfillment,
  index,
  open,
  onOpenChange,
}: EditFulfillmentDialogProps) {
  const { t } = useTranslation(['orders', 'common']);
  const message = useErrorMessage();
  const update = useUpdateFulfillment(storeId);
  const form = useForm<FulfillmentUpdateInput, unknown, FulfillmentUpdateOutput>({
    resolver: useI18nResolver(fulfillmentUpdateSchema),
    defaultValues: fromFulfillment(fulfillment),
  });

  const close = () => {
    onOpenChange(false);
    update.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    const body = toFulfillmentUpdate(values, fulfillment);
    if (Object.keys(body).length === 0) {
      close();
      return;
    }
    try {
      await update.mutateAsync({ id: order.id, fulfillmentId: fulfillment.id, body });
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      const conflict = orderConflictKey(error, 'orders:fulfillments.edit.conflict');
      form.setError('root.server', {
        type: 'server',
        message: conflict ? t(conflict) : message(error),
      });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('orders:fulfillments.edit.title', { index: index + 1, name: order.name })}
          </DialogTitle>
          <DialogDescription>{t('orders:fulfillments.edit.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="carrier"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('orders:fulfil.carrier')}</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" placeholder="UPS, FedEx, DHL…" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="trackingNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('orders:fulfil.trackingNumber')}</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="trackingUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('orders:fulfil.trackingUrl')}</FormLabel>
                  <FormControl>
                    <Input type="url" autoComplete="off" placeholder="https://" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('orders:fulfil.note')}</FormLabel>
                  <FormControl>
                    <Textarea rows={2} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="notifyCustomer"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start justify-between gap-4 rounded-md border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>{t('orders:fulfillments.edit.notify')}</FormLabel>
                    <FormDescription>{t('orders:fulfillments.edit.notifyHint')}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? t('common:actions.saving') : t('common:actions.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
